// Időkapu-foglaló · iter146 — Kapu-szkenner (Gate lookup).
//
// Dock-admin-only page at /dock-scheduling/gate. Lets plant staff:
//   1. Type or scan a reference number (e.g. IK-2026-000123)
//   2. See the booking details + slot ticket
//   3. Mark the arriving vehicle as Érkezett / Nem jelent meg
//   4. Reset to scan the next one
//
// The "scan" is intentionally low-tech: an <input autoFocus /> captures the
// keyboard output of a USB barcode/QR scanner, then Enter triggers lookup.

import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { ScanLine, Search, X, CheckCircle2, XCircle, RotateCcw, Ticket, AlertTriangle } from "lucide-react";
import SlotTicket, { PrintButton } from "./DockSlotTicket";
import ArrivalPill from "./DockArrivalPill";
import BookingAudit from "./DockBookingAudit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dock-scheduling`;
const BLUE = "#185FA5";

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});
const syne = (size, weight) => ({ fontFamily: "Syne, sans-serif", fontSize: size, fontWeight: weight || 700 });

export default function DockGateLookup() {
  const [ref, setRef] = useState("");
  const [booking, setBooking] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const lookup = useMutation({
    mutationFn: (r) => axios.get(`${API}/gate/lookup`, { params: { ref: r } }).then(res => res.data),
    onSuccess: (data) => {
      setBooking(data);
    },
    onError: (e) => {
      setBooking(null);
      toast.error(e?.response?.data?.detail || "Nem sikerült megtalálni a foglalást");
    },
  });

  const arrival = useMutation({
    mutationFn: ({ id, status }) => axios.post(`${API}/bookings/${id}/arrival`, { status }).then(r => r.data),
    onSuccess: (data) => {
      setBooking(data);
      const label = data.arrival_status === "late" ? "Késve érkezett" :
                    data.arrival_status === "arrived" ? "Érkezett" :
                    data.arrival_status === "no_show" ? "Nem jelent meg" : "Frissítve";
      toast.success(label);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Hiba a jelöléskor"),
  });

  const submit = (e) => {
    e && e.preventDefault();
    const v = ref.trim();
    if (!v) return;
    lookup.mutate(v);
  };

  const reset = () => {
    setRef("");
    setBooking(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const cancelled = booking?.status === "cancelled";

  return (
    <div data-testid="ds-gate-lookup" className="p-1">
      <header className="mb-4">
        <h1 style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: "clamp(22px, 3vw, 30px)", color: "var(--text, #f0f2f8)", letterSpacing: "-0.5px" }} data-testid="view-title">
          <ScanLine size={26} color={BLUE} style={{ display: "inline", verticalAlign: -4, marginRight: 10 }} />
          Kapu-szkenner
        </h1>
        <div className="mt-1.5" style={mono(11, "var(--text-dim, #c5cad8)")}>
          Foglalás keresése referencia-szám alapján · QR-olvasó vagy kézi beírás
        </div>
      </header>

      {/* Scanner input */}
      <form
        onSubmit={submit}
        className="mb-4 rounded-md p-3"
        style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(148,163,184,0.15)" }}
      >
        <label className="block">
          <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>REFERENCIA-SZÁM</div>
          <div className="mt-1 flex items-center gap-2">
            <input
              ref={inputRef}
              data-testid="ds-gate-lookup-input"
              value={ref}
              onChange={(e) => setRef(e.target.value.toUpperCase())}
              placeholder="pl. IK-2026-000123"
              className="px-3 py-2 rounded flex-1"
              autoFocus
              style={{
                background: "var(--bg-input, #0a0b12)",
                border: `1px solid ${BLUE}`,
                color: "var(--text, #f0f2f8)",
                ...mono(16, undefined, 700, "1px"),
              }}
            />
            <button
              type="submit"
              data-testid="ds-gate-lookup-search"
              disabled={!ref.trim() || lookup.isPending}
              className="px-3 py-2 rounded inline-flex items-center gap-1.5"
              style={{ background: BLUE, color: "#fff", border: 0, cursor: "pointer", ...mono(11, "#fff", 700, "0.4px") }}
            >
              <Search size={14} /> KERESÉS
            </button>
            {booking && (
              <button
                type="button"
                onClick={reset}
                data-testid="ds-gate-lookup-reset"
                className="px-3 py-2 rounded inline-flex items-center gap-1.5"
                style={{ background: "transparent", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", cursor: "pointer", ...mono(11, undefined, 700, "0.4px") }}
              >
                <RotateCcw size={13} /> ÚJ SCAN
              </button>
            )}
          </div>
        </label>
      </form>

      {/* Result */}
      {booking && (
        <div data-testid="ds-gate-lookup-result" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-md p-4"
            style={{ background: "var(--bg-elevated)", color: "var(--text)", border: `1px solid ${BLUE}` }}
          >
            <div className="flex items-baseline gap-2 mb-2">
              <Ticket size={14} color={BLUE} />
              <div style={syne(14, 800)}>Foglalás</div>
              <div className="flex-1" />
              <ArrivalPill status={booking.arrival_status} testId={`ds-arrival-status-${booking.id}`} />
            </div>

            <div className="mb-3" style={{ ...mono(24, BLUE, 800, "1.5px") }}>
              {booking.reference}
            </div>

            {cancelled && (
              <div className="rounded p-2 mb-3" style={{ background: "rgba(248,113,113,0.10)", border: "1px solid rgba(248,113,113,0.30)", ...mono(11, "#f87171") }}>
                <AlertTriangle size={12} style={{ display: "inline", verticalAlign: -2, marginRight: 4 }} />
                LEMONDVA · érkezés nem jelölhető
              </div>
            )}

            <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
              <tbody>
                {[
                  ["Idősáv", `${booking.slot_start_local}–${booking.slot_end_local}`],
                  ["Nap", (booking.slot_start_local_iso || booking.slot_start || "").slice(0, 10)],
                  ["Dokk", booking.dock_name || booking.dock_id],
                  ["Kapu", booking.gate_code || "—"],
                  ["Irány", booking.direction === "inbound" ? "Bejövő" : booking.direction === "outbound" ? "Kimenő" : "—"],
                  ["Rendszám", booking.rendszam || "—"],
                  ["Pótkocsi", booking.field_values?.potkocsi_rendszam || "—"],
                  ["Szolgáltató", booking.szolgaltato_neve || "—"],
                  ["Sofőr", booking.field_values?.sofor_neve || "—"],
                  ["Sofőr tel.", booking.field_values?.sofor_telefon || "—"],
                  ["Vonal", booking.vonal || "—"],
                  ["TO szám", booking.field_values?.to_szam || "—"],
                ].map(([k, v]) => (
                  <tr key={k} style={{ borderBottom: "1px dashed rgba(148,163,184,0.20)" }}>
                    <td className="py-1.5 pr-3" style={mono(10, "var(--dimmer)", 700, "0.4px")}>{k.toUpperCase()}</td>
                    <td className="py-1.5" style={mono(12)}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Arrival actions */}
            {!cancelled && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  data-testid={`ds-arrival-arrived-${booking.id}`}
                  onClick={() => arrival.mutate({ id: booking.id, status: "arrived" })}
                  disabled={arrival.isPending}
                  className="px-3 py-2 rounded inline-flex items-center gap-1.5"
                  style={{ background: "#22c55e", color: "#fff", border: 0, cursor: "pointer", ...mono(12, "#fff", 700, "0.4px") }}
                >
                  <CheckCircle2 size={14} /> ÉRKEZETT
                </button>
                <button
                  type="button"
                  data-testid={`ds-arrival-noshow-${booking.id}`}
                  onClick={() => window.confirm("Nem jelent meg jelölést rögzítesz?") && arrival.mutate({ id: booking.id, status: "no_show" })}
                  disabled={arrival.isPending}
                  className="px-3 py-2 rounded inline-flex items-center gap-1.5"
                  style={{ background: "#f87171", color: "#fff", border: 0, cursor: "pointer", ...mono(12, "#fff", 700, "0.4px") }}
                >
                  <XCircle size={14} /> NEM JELENT MEG
                </button>
                {["arrived", "late", "no_show"].includes(booking.arrival_status) && (
                  <button
                    type="button"
                    data-testid={`ds-arrival-reset-${booking.id}`}
                    onClick={() => arrival.mutate({ id: booking.id, status: "pending" })}
                    disabled={arrival.isPending}
                    className="px-2 py-1 rounded inline-flex items-center gap-1.5"
                    style={{ background: "transparent", border: "1px solid rgba(148,163,184,0.30)", color: "var(--dimmer)", cursor: "pointer", ...mono(10, undefined, 700, "0.4px") }}
                  >
                    <RotateCcw size={11} /> Visszaállít
                  </button>
                )}
                <div className="flex-1" />
                <PrintButton />
              </div>
            )}

            {/* iter147 · Audit trail (default open — gate staff wants to see it immediately) */}
            <BookingAudit bookingId={booking.id} defaultOpen={true} />
          </div>

          {/* Slot ticket preview */}
          <div>
            <SlotTicket booking={booking} plantName={booking.plant_name} />
          </div>
        </div>
      )}

      {!booking && !lookup.isPending && (
        <div className="rounded p-4" style={{ background: "var(--bg-surface)", border: "1px dashed rgba(148,163,184,0.30)", ...mono(12, "var(--dimmer)") }}>
          Kezdd el a szkennelést vagy írd be a referencia-számot a fenti mezőbe.
        </div>
      )}
    </div>
  );
}
